/**
 * RestroPulse POS · Demo seed
 * ─────────────────────────────────────────────────────────────────────
 * Pre-baked state snapshot for /pos-demo. A visitor arriving without
 * login sees a working restaurant on frame one — a couple of occupied
 * tables mid-service, a billed table waiting for payment, tokens in
 * the queue for the takeaway counter, and business day + shift both
 * already open so tapping any table starts billing immediately.
 *
 * Everything below is fictional. No real outlet, tenant or user name
 * is referenced. State lives entirely in memory; two visitors never
 * see each other's actions.
 */
import { initialState, ORDER_STATE, ORDER_TYPE } from "./posReducer";
import { ITEMS } from "./mockData";

// Deterministic id helper so the same seed produces the same order
// ids on every reset (helpful when writing e2e scripts against demo).
let seq = 0;
const did = (p) => `${p}_${++seq}`;

const findItem = (id) => ITEMS.find((x) => x.id === id) || ITEMS[0];
const line = (item_id, qty, opts = {}) => {
  const it = findItem(item_id);
  return {
    id: did("ln"),
    item_id: it.id,
    item_name: it.name,
    qty,
    unit_price: it.price,
    variant: null,
    addons: [],
    note: "",
    discount: 0,
    ticket_id: opts.ticket_id || null,
    ticket_fired_at: opts.ticket_fired_at || null,
    ticket_ready_at: opts.ticket_ready_at || null,
  };
};

export function buildDemoState() {
  seq = 0;
  const now = Date.now();
  const yyyymmdd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  // Build a fresh baseline off initialState so any new session keys
  // added elsewhere in the reducer flow through automatically.
  const base = JSON.parse(JSON.stringify(initialState));

  // ─── SESSION · business day + shift already open ─────────────────
  base.session.business_date          = yyyymmdd(new Date(now));
  base.session.business_day_opened_at = now - 4 * 3600_000;   // 4h ago
  base.session.business_day_opened_by = "mgr_demo_01";
  base.session.business_day_closed_at = null;
  base.session.business_day_closed_by = null;
  base.session.shifts = [{
    id: did("shift"),
    opened_at:     now - 4 * 3600_000,
    opened_by:     "cash_demo_01",
    opening_float: 2500,
    closed_at:     null,
    closed_by:     null,
    counted_cash:  0,
    expected_cash: 0,
    variance:      0,
  }];
  base.session.ticket_seq  = { kitchen: 4, bar: 1 };
  base.session.invoice_seq = 1;

  // ─── ORDER 1 · Table T3 · dine-in · unfired lines (OPEN) ────────
  const ord1_id = did("ord");
  const ord1 = {
    order: {
      id: ord1_id, state: ORDER_STATE.OPEN, type: ORDER_TYPE.DINE_IN,
      table_id: "t_g03", covers: 3, steward_id: "s_priya",
      opened_at: now - 25 * 60_000,
      invoice_no: null,
    },
    lines: [
      line("i_paneer_tikka",    1),
      line("i_veg_biryani",     1),
      line("i_masala_papad",    2),
    ],
    payments: [],
  };

  // ─── ORDER 2 · Table T7 · dine-in · KOT fired, food PREPARING ──
  const ord2_id = did("ord");
  const ord2 = {
    order: {
      id: ord2_id, state: ORDER_STATE.OPEN, type: ORDER_TYPE.DINE_IN,
      table_id: "t_g07", covers: 4, steward_id: "s_arjun",
      opened_at: now - 40 * 60_000,
      invoice_no: null,
    },
    lines: [
      line("i_chicken_biryani", 2, { ticket_id: "KOT_1", ticket_fired_at: now - 30 * 60_000 }),
      line("i_hara_bhara_kabab",1, { ticket_id: "KOT_1", ticket_fired_at: now - 30 * 60_000 }),
      line("i_kingfisher",      2, { ticket_id: "BOT_1", ticket_fired_at: now - 30 * 60_000 }),
    ],
    payments: [],
  };

  // ─── ORDER 3 · Table T2 · dine-in · BILL PRINTED (settlement pending)
  const ord3_id = did("ord");
  const ord3 = {
    order: {
      id: ord3_id, state: ORDER_STATE.PRINTED, type: ORDER_TYPE.DINE_IN,
      table_id: "t_g02", covers: 2, steward_id: "s_priya",
      opened_at: now - 65 * 60_000,
      printed_at: now - 3 * 60_000,
      invoice_no: "GB/25-26/001",
    },
    lines: [
      line("i_paneer_tikka",    2, { ticket_id: "KOT_2", ticket_fired_at: now - 60 * 60_000, ticket_ready_at: now - 50 * 60_000 }),
      line("i_gulab_jamun",     2, { ticket_id: "KOT_3", ticket_fired_at: now - 40 * 60_000, ticket_ready_at: now - 35 * 60_000 }),
      line("i_masala_chai",     3, { ticket_id: "KOT_3", ticket_fired_at: now - 40 * 60_000, ticket_ready_at: now - 35 * 60_000 }),
    ],
    payments: [],
  };

  // ─── ORDER 4 · Takeaway ready for pickup · token T-001 ────────
  const ord4_id = did("ord");
  const ord4 = {
    order: {
      id: ord4_id, state: ORDER_STATE.OPEN, type: ORDER_TYPE.TAKEAWAY,
      table_id: null, covers: 1, steward_id: null,
      opened_at: now - 20 * 60_000,
      invoice_no: null,
      token: "T-001",
      guest_name:  "Aditi Rao",
      guest_phone: "9876500001",
    },
    lines: [
      line("i_chicken_biryani", 1, { ticket_id: "KOT_4", ticket_fired_at: now - 18 * 60_000, ticket_ready_at: now - 6 * 60_000 }),
      line("i_paneer_tikka",    1, { ticket_id: "KOT_4", ticket_fired_at: now - 18 * 60_000, ticket_ready_at: now - 6 * 60_000 }),
    ],
    payments: [],
  };

  // ─── ORDER 5 · Takeaway still cooking · token T-002 ────────
  const ord5_id = did("ord");
  const ord5 = {
    order: {
      id: ord5_id, state: ORDER_STATE.OPEN, type: ORDER_TYPE.TAKEAWAY,
      table_id: null, covers: 1, steward_id: null,
      opened_at: now - 6 * 60_000,
      invoice_no: null,
      token: "T-002",
      guest_name:  "Vikram Shah",
      guest_phone: "9876500002",
    },
    lines: [
      line("i_paneer_tikka",    1, { ticket_id: "KOT_5", ticket_fired_at: now - 4 * 60_000 }),
      line("i_gulab_jamun",     2, { ticket_id: "KOT_5", ticket_fired_at: now - 4 * 60_000 }),
    ],
    payments: [],
  };

  base.orders = {
    [ord1_id]: ord1,
    [ord2_id]: ord2,
    [ord3_id]: ord3,
    [ord4_id]: ord4,
    [ord5_id]: ord5,
  };

  // Token queue snapshot — one ready, one preparing.
  base.session.token_seq = 2;
  base.session.tokens = [
    {
      number: 1, label: "T-001", order_id: ord4_id, state: "ready",
      order_type: "TAKEAWAY", guest_name: "Aditi Rao",
      allocated_at: now - 20 * 60_000,
      ready_at:     now - 6  * 60_000,
    },
    {
      number: 2, label: "T-002", order_id: ord5_id, state: "preparing",
      order_type: "TAKEAWAY", guest_name: "Vikram Shah",
      allocated_at: now - 6 * 60_000,
    },
  ];

  base.active_order_id = null;
  return base;
}
